import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAnniversary } from '../../context/AnniversaryContext';
import Section from '../common/Section';

const MetricsSection: React.FC = () => {
  const { data } = useAnniversary();
  
  return (
    <Section id="metrics" title="Our Story in Numbers">
      <MetricsContainer>
        {data.metrics.map((metric, index) => (
          <MetricCard 
            key={metric.id}
            as={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ 
              opacity: 1, 
              y: 0,
              transition: { duration: 0.5, delay: index * 0.1 } 
            }}
            viewport={{ once: true, amount: 0.3 }}
            whileHover={{ 
              y: -10, 
              boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
              transition: { duration: 0.3 }
            }}
          >
            <AnimatedCounter value={Number(metric.value)} />
            <MetricLabel>{metric.label}</MetricLabel>
          </MetricCard>
        ))}
      </MetricsContainer>
    </Section>
  );
};

interface AnimatedCounterProps {
  value: number;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ value }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef<HTMLDivElement>(null);
  
  // Use Intersection Observer to trigger animation when element is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start animation when element is visible
            let startValue = 0;
            const duration = 2000; // Animation duration in ms
            const increment = value / (duration / 16); // Update every ~16ms (60fps)
            
            const timer = setInterval(() => {
              startValue += increment;
              if (startValue >= value) {
                clearInterval(timer);
                setCount(value);
              } else {
                setCount(Math.floor(startValue));
              }
            }, 16);
            
            // Cleanup observer after animation starts
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (countRef.current) {
      observer.observe(countRef.current);
    }
    
    return () => observer.disconnect();
  }, [value]);
  
  return (
    <MetricValue ref={countRef}>
      <span>{count}</span>
    </MetricValue>
  );
};

const MetricsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-top: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const MetricCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.cardBackground};
  backdrop-filter: blur(8px);
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform ${({ theme }) => theme.transitions.normal}, 
              box-shadow ${({ theme }) => theme.transitions.normal};
  overflow: hidden;
  position: relative;
  
  /* K-pop style decorative element */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 120%;
    height: 120%;
    background: linear-gradient(
      to bottom right,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0)
    );
    transform: rotate(45deg);
    pointer-events: none;
    z-index: 1;
  }
`;

const MetricValue = styled.div`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  
  span {
    background: ${({ theme }) => theme.gradients.secondary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }
  
  /* K-pop style decorative glow */
  &::after {
    content: '';
    position: absolute;
    width: 50px;
    height: 50px;
    background: ${({ theme }) => `radial-gradient(circle, ${theme.colors.accent1}40, transparent 70%)`};
    bottom: 0;
    right: 0;
    border-radius: 50%;
    filter: blur(10px);
    z-index: 0;
  }
`;

const MetricLabel = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.light};
  position: relative;
  z-index: 2;
`;

export default MetricsSection; 